import type {
  ExpenseBudgetCategory,
  TopProject,
} from "@src/__generated__/urql-graphql";
import { currency } from "@src/utils/formatters";
import type { Prettify } from "@src/utils/types";
import { action, computed, makeObservable, observable } from "mobx";
import type { TopProjectsGraphProps } from ".";

type TopProjectForGraph = Omit<TopProject, "expense_budget_categories"> &
  Prettify<Record<ExpenseBudgetCategory["name"], number>>;

export class TopProjectGraphStore {
  readonly colors = ["#9DE490", "#EBCE64", "#FFA78B"];
  @observable data: TopProjectsGraphProps["data"];
  @observable
  expenseBudgetCategories: TopProjectsGraphProps["expenseBudgetCategories"];

  constructor(
    data: TopProjectsGraphProps["data"],
    expenseBudgetCategories: TopProjectsGraphProps["expenseBudgetCategories"],
  ) {
    makeObservable(this);
    this.data = data;
    this.expenseBudgetCategories = expenseBudgetCategories;
  }

  @action setData(data: TopProjectsGraphProps["data"]) {
    this.data = data;
  }

  @computed get dataForGraph(): TopProjectForGraph[] {
    const newData: TopProjectForGraph[] = [];
    const dataCount = this.data.length;
    const expenseCategoryCount = this.expenseBudgetCategories.length;

    for (let i = 0; i < dataCount; i++) {
      const item = this.data[i];
      if (!item) continue;

      const simplifiedItem = new Map();

      simplifiedItem.set("title", item.title);
      simplifiedItem.set("total_value", item.total_value);
      simplifiedItem.set(
        "total_value_formatted",
        currency.formatByWorkspace(item.total_value, {
          fractionDigits: 0,
        }),
      );
      simplifiedItem.set("our_work_value", item.our_work.value);

      for (let j = 0; j < expenseCategoryCount; j++) {
        const category = this.expenseBudgetCategories[j];

        if (!category) continue;

        const itemCategory = item.expense_budget_categories?.find(
          (itemCategory) =>
            itemCategory?.expense_budget_item_id === category.id,
        );

        if (!itemCategory) continue;

        simplifiedItem.set(category.name, itemCategory.value);
      }
      newData.push(Object.fromEntries(simplifiedItem));
    }

    return newData.toSorted((a, b) => (a.total_value < b.total_value ? 1 : -1));
  }
}
